
import React, { useState, useEffect, useRef } from 'react';
/* Added LayoutGrid to imports */
import { Menu, X, ChevronRight, ChevronLeft, ChevronDown, Phone, Mail, MapPin, ArrowRight, CheckCircle2, Calendar, Building2, Send, AlertCircle, Quote, Eye, Target, Shield, Award, Users, Lightbulb, ThumbsUp, ArrowUp, Briefcase, Leaf, Facebook, Twitter, Linkedin, Instagram, LayoutGrid } from 'lucide-react';
import { NavItem, ProjectItem } from './types';
import {
  COMPANY_INFO,
  NAV_ITEMS,
  HERO_SLIDES,
  ABOUT_DATA,
  WHY_CHOOSE_US,
  CAPABILITIES,
  SERVICES,
  STATS,
  PROJECTS,
  TESTIMONIALS,
} from './data';

// --- Global Lenis Instance ---
declare global {
  interface Window {
    Lenis: any;
  }
}

let globalLenis: any = null;

// --- Utility: Reveal Animation Component ---
interface RevealProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  threshold?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  blur?: boolean;
}

const RevealOnScroll: React.FC<RevealProps> = ({
  children,
  className = "",
  delay = 0,
  duration = 1500,
  threshold = 0.1,
  direction = 'up',
  blur = true
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (ref.current) observer.unobserve(ref.current);
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => { if (ref.current) observer.unobserve(ref.current); };
  }, [threshold]);

  const getInitialTransform = () => {
    switch (direction) {
      case 'up': return 'translate-y-24';
      case 'down': return '-translate-y-24';
      case 'left': return '-translate-x-24';
      case 'right': return 'translate-x-24';
      default: return 'translate-y-24';
    }
  };

  const transformClass = isVisible ? 'translate-x-0 translate-y-0 opacity-100 blur-0' : `${getInitialTransform()} opacity-0 ${blur ? 'blur-sm' : ''}`;

  return (
    <div
      ref={ref}
      className={`transition-all will-change-transform ease-[cubic-bezier(0.22,1,0.36,1)] ${transformClass} ${className}`}
      style={{ transitionDuration: `${duration}ms`, transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// --- Header Component ---
const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      const scrollPos = window.scrollY + 200;

      const sections = NAV_ITEMS.map(item => item.href.substring(1));
      NAV_ITEMS.forEach(item => {
        if (item.children) {
          item.children.forEach(child => sections.push(child.href.substring(1)));
        }
      });

      for (const id of sections) {
        const el = document.getElementById(id);
        if (el && scrollPos >= el.offsetTop && scrollPos < el.offsetTop + el.offsetHeight) {
          setActiveSection(id);
        }
      }
      if (window.scrollY < 100) setActiveSection('home');
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const targetId = href.startsWith('#') ? href.substring(1) : '';
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      setIsMobileMenuOpen(false);
      if (globalLenis) {
        globalLenis.scrollTo(targetElement, { offset: -80, duration: 2.2 });
      } else {
        targetElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header className={`fixed top-0 w-full z-40 transition-all duration-700 ${isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-2 border-b border-gray-100' : 'bg-transparent py-6'}`}>
      <div className={`hidden lg:block container mx-auto px-6 mb-2 transition-all duration-500 origin-top ${isScrolled ? 'opacity-0 h-0 overflow-hidden scale-y-0' : 'opacity-100 scale-y-100'}`}>
        <div className="flex justify-end gap-6 text-sm text-gray-200">
          <span className="flex items-center gap-2"><Phone size={14} /> {COMPANY_INFO.phone}</span>
          <span className="flex items-center gap-2"><Mail size={14} /> {COMPANY_INFO.email}</span>
        </div>
      </div>

      <div className="container mx-auto px-6 flex justify-between items-center">
        <a href="#home" onClick={(e) => handleNavClick(e, '#home')} className="flex items-center gap-2 group">
          <div className="w-10 h-10 bg-blue-900 text-white flex items-center justify-center font-bold text-xl rounded-sm transition-transform group-hover:scale-110">{COMPANY_INFO.logoChar}</div>
          <span className={`text-xl font-bold uppercase tracking-wider transition-colors duration-300 ${isScrolled ? 'text-blue-900' : 'text-white'}`}>{COMPANY_INFO.name}</span>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_ITEMS.map((item) => (
            <div key={item.label} className="relative group">
              <a
                href={item.href} onClick={(e) => handleNavClick(e, item.href)}
                className={`text-sm font-semibold uppercase tracking-wide transition-colors py-2 flex items-center gap-1 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-yellow-500 after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300 ${activeSection === item.href.substring(1) ? 'text-yellow-500 after:scale-x-100' : isScrolled ? 'text-slate-700 hover:text-yellow-500' : 'text-white hover:text-yellow-500'}`}
              >
                {item.label} {item.children && <ChevronDown size={14} />}
              </a>
              {item.children && (
                <div className="absolute top-full left-0 pt-4 w-56 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 z-50">
                  <div className="bg-white rounded-md shadow-xl border-t-4 border-yellow-500 py-2 overflow-hidden">
                    {item.children.map(child => (
                      <a key={child.label} href={child.href} onClick={(e) => handleNavClick(e, child.href)} className="block px-6 py-3 text-sm font-medium text-slate-700 hover:bg-gray-50 hover:text-yellow-600 transition-colors border-b border-gray-50 last:border-0">{child.label}</a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          <a href="#contact" onClick={(e) => handleNavClick(e, '#contact')} className="bg-gradient-to-r from-red-700 to-red-800 hover:from-red-600 hover:to-red-700 text-white px-6 py-2.5 rounded-sm text-sm font-bold shadow-lg transition-all hover:-translate-y-0.5">Get Quote</a>
        </nav>

        <button className={`lg:hidden transition-colors ${isScrolled ? 'text-slate-800' : 'text-white'}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div className="absolute top-full left-0 w-full bg-white shadow-lg py-4 px-6 flex flex-col lg:hidden border-t border-gray-100 animate-fadeIn max-h-[80vh] overflow-y-auto">
          {NAV_ITEMS.map(item => (
            <React.Fragment key={item.label}>
              <a href={item.href} onClick={(e) => handleNavClick(e, item.href)} className="font-bold py-4 border-b border-gray-100 text-slate-800">{item.label}</a>
              {item.children?.map(child => (
                <a key={child.label} href={child.href} onClick={(e) => handleNavClick(e, child.href)} className="block py-3 text-sm pl-6 text-slate-600 border-b border-gray-50">{child.label}</a>
              ))}
            </React.Fragment>
          ))}
        </div>
      )}
    </header>
  );
};

// --- Hero Component ---
const Hero: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    const interval = setInterval(() => setCurrentSlide(prev => (prev + 1) % HERO_SLIDES.length), 7000);
    return () => { window.removeEventListener('scroll', handleScroll); clearInterval(interval); };
  }, []);

  return (
    <section id="home" className="relative h-screen min-h-[700px] flex items-center justify-center overflow-hidden bg-slate-900">
      {HERO_SLIDES.map((slide, index) => (
        <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
          <div className="absolute inset-0">
            <div className="absolute w-full h-[120%] -top-[10%] left-0 will-change-transform" style={{ transform: `translateY(${scrollY * 0.45}px)` }}>
              <img src={slide.image} alt={slide.tag} className={`w-full h-full object-cover transition-transform duration-[10000ms] ${index === currentSlide ? 'scale-110' : 'scale-100'}`} />
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/40 to-transparent"></div>
          </div>
          <div className="relative h-full container mx-auto px-6 flex items-center">
            <div className="max-w-4xl text-left">
              <RevealOnScroll direction="up" delay={300} duration={1800}>
                <span className="inline-block py-2 px-5 bg-blue-900/80 text-yellow-400 border border-yellow-500/30 rounded-full text-xs font-bold uppercase tracking-widest mb-8 backdrop-blur-sm shadow-xl">{slide.tag}</span>
                <h1 className="text-6xl md:text-8xl lg:text-9xl font-bold text-white leading-[1.1] mb-8 tracking-tight">
                  {slide.titlePrefix} <br /> <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">{slide.titleHighlight}</span> <br /> {slide.titleSuffix}
                </h1>
                <p className="text-gray-200 text-xl md:text-2xl mb-12 max-w-2xl leading-relaxed font-light">{slide.subtitle}</p>
                <div className="flex flex-col sm:flex-row gap-6">
                  <a href="#projects" onClick={(e) => { e.preventDefault(); globalLenis?.scrollTo('#projects', { duration: 2.2 }); }} className="bg-red-700 hover:bg-red-600 text-white px-12 py-5 rounded-sm font-bold flex items-center justify-center gap-3 transition-all hover:scale-105 hover:shadow-2xl shadow-red-900/20 group text-lg">Our Projects <ArrowRight className="group-hover:translate-x-1 transition-transform" /></a>
                  <a href="#contact" onClick={(e) => { e.preventDefault(); globalLenis?.scrollTo('#contact', { duration: 2.2 }); }} className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-12 py-5 rounded-sm font-bold hover:bg-white hover:text-slate-900 transition-all hover:scale-105 text-lg">Get a Quote</a>
                </div>
              </RevealOnScroll>
            </div>
          </div>
        </div>
      ))}
      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 flex gap-4">
        {HERO_SLIDES.map((_, i) => (
          <button key={i} onClick={() => setCurrentSlide(i)} className={`h-1.5 rounded-full transition-all duration-500 ${i === currentSlide ? 'bg-yellow-500 w-16' : 'bg-white/30 w-6 hover:bg-white/50'}`} />
        ))}
      </div>
    </section>
  );
};

// --- About Section ---
const AboutSection: React.FC = () => (
  <section id="about" className="py-32 bg-white overflow-hidden">
    <div className="container mx-auto px-6">
      <div className="flex flex-col lg:flex-row items-center gap-20">
        <div className="lg:w-1/2">
          <RevealOnScroll direction="right">
            <div className="relative">
              <div className="relative z-10 overflow-hidden rounded-xl shadow-2xl">
                <img src={ABOUT_DATA.image} alt="Industrial Construction" className="w-full h-auto transition-transform duration-1000 hover:scale-105" />
              </div>
              <div className="absolute -bottom-10 -right-10 bg-blue-900 text-white p-10 rounded-xl shadow-2xl border-l-8 border-yellow-500 hidden xl:block z-20">
                <p className="text-6xl font-black mb-1">{ABOUT_DATA.experienceYears}</p>
                <p className="text-sm uppercase tracking-[0.2em] font-bold text-blue-200">{ABOUT_DATA.experienceText}</p>
              </div>
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-yellow-500/10 rounded-full blur-3xl -z-10"></div>
            </div>
          </RevealOnScroll>
        </div>
        <div className="lg:w-1/2">
          <RevealOnScroll direction="left" delay={200}>
            <h4 className="text-yellow-600 font-bold uppercase tracking-widest mb-4 text-sm flex items-center gap-3">
              <span className="w-8 h-0.5 bg-yellow-600"></span> Who We Are
            </h4>
            <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-8 leading-tight tracking-tight">
              {ABOUT_DATA.title} <br /><span className="text-blue-900">{ABOUT_DATA.titleHighlight}</span>
            </h2>
            <p className="text-slate-600 mb-8 leading-relaxed text-xl font-light">{ABOUT_DATA.description1}</p>
            <p className="text-slate-600 mb-12 leading-relaxed">{ABOUT_DATA.description2}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="p-8 bg-slate-50 rounded-xl border-t-4 border-yellow-500 shadow-sm hover:shadow-lg transition-shadow">
                <h3 className="font-bold text-2xl mb-4 flex items-center gap-3 text-slate-900"><Eye className="text-yellow-600" size={28} /> Our Vision</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{ABOUT_DATA.vision}</p>
              </div>
              <div className="p-8 bg-blue-900 text-white rounded-xl border-t-4 border-yellow-500 shadow-xl">
                <h3 className="font-bold text-2xl mb-4 flex items-center gap-3 text-yellow-400"><Target size={28} /> Our Mission</h3>
                <p className="text-blue-100 leading-relaxed text-sm">{ABOUT_DATA.mission}</p>
              </div>
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </div>
  </section>
);

// --- Why Choose Us Section ---
const WhyChooseUsSection: React.FC = () => (
  <section id="why-choose-us" className="py-24 bg-blue-900 text-white text-center relative overflow-hidden">
    <div className="absolute top-0 right-0 w-96 h-96 bg-yellow-500/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2"></div>
    <div className="container mx-auto px-6 relative z-10">
      <RevealOnScroll direction="up">
        <h4 className="text-yellow-500 font-bold uppercase tracking-widest mb-4 text-sm">Our Advantage</h4>
        <h2 className="text-4xl lg:text-5xl font-bold mb-16 tracking-tight">Why Choose Azmi Gulf</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {WHY_CHOOSE_US.map((item, i) => (
            <div key={i} className="bg-blue-800/40 backdrop-blur-md p-10 rounded-2xl border border-white/10 hover:bg-yellow-500 hover:text-blue-900 transition-all group duration-500 hover:-translate-y-3">
              <div className="w-20 h-20 bg-blue-700/50 rounded-2xl flex items-center justify-center text-yellow-400 mx-auto mb-8 group-hover:bg-blue-900 transition-colors shadow-lg"><item.icon size={40} /></div>
              <h3 className="text-2xl font-bold mb-4 tracking-tight">{item.title}</h3>
              <p className="text-blue-200 text-sm leading-relaxed group-hover:text-blue-900/80 font-medium">{item.description}</p>
            </div>
          ))}
        </div>
      </RevealOnScroll>
    </div>
  </section>
);

// --- Sustainability Section ---
const SustainabilitySection: React.FC = () => (
  <section id="sustainability" className="py-32 bg-slate-50 relative">
    <div className="container mx-auto px-6">
      <div className="flex flex-col lg:flex-row items-center gap-16">
        <div className="lg:w-1/2">
          <RevealOnScroll direction="right">
            <h4 className="text-yellow-600 font-bold uppercase tracking-widest mb-4 text-sm">Responsibility</h4>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-8 tracking-tight">Commitment to Sustainability</h2>
            <p className="text-slate-600 leading-relaxed mb-10 text-lg font-light">At Azmi Gulf, sustainable practices are not just a policy—they are fundamental to our long-term success and corporate identity.</p>
            <div className="space-y-6">
              {["Environmental Compliance & Protection", "Sustainable Resource Management", "Community Engagement", "Zero-Harm HSE Culture"].map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow group">
                  <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition-colors"><Leaf size={20} /></div>
                  <span className="font-bold text-slate-800">{item}</span>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </div>
        <div className="lg:w-1/2">
          <RevealOnScroll direction="left" delay={300}>
            <div className="relative">
              <div className="bg-white p-12 rounded-2xl shadow-2xl border-t-8 border-green-500 relative z-10">
                <div className="flex items-center gap-6 mb-8">
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-2xl flex items-center justify-center shadow-inner"><Shield size={36} /></div>
                  <div>
                    <h3 className="text-2xl font-bold text-slate-900">HSE Policy</h3>
                    <p className="text-green-600 font-bold text-sm uppercase tracking-widest">Our Top Priority</p>
                  </div>
                </div>
                <p className="italic text-slate-600 text-xl leading-relaxed mb-10 font-serif">"We provide a safe and healthy workplace for all employees, ensuring zero harm to people and the environment in every project we execute."</p>
                <div className="grid grid-cols-3 gap-6 pt-10 border-t border-slate-100 text-center">
                  <div><p className="text-2xl font-black text-slate-900">ISO</p><p className="text-xs text-slate-500 font-bold">14001:2015</p></div>
                  <div><p className="text-2xl font-black text-slate-900">ISO</p><p className="text-xs text-slate-500 font-bold">45001:2018</p></div>
                  <div><p className="text-2xl font-black text-yellow-600">ZERO</p><p className="text-xs text-slate-500 font-bold">LTI TARGET</p></div>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-green-500/10 rounded-full blur-2xl -z-10"></div>
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </div>
  </section>
);

// --- Capabilities (What We Do) Section ---
const WhatWeDoSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  /* Use a capitalized variable for the dynamic component to fix the construction error */
  const ActiveIcon = CAPABILITIES[activeTab].icon;

  return (
    <section id="what-we-do" className="py-32 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-20">
          <RevealOnScroll direction="up">
            <h4 className="text-yellow-600 font-bold uppercase tracking-widest mb-4 text-sm">Capabilities</h4>
            <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 tracking-tight">Comprehensive Solutions</h2>
          </RevealOnScroll>
        </div>
        <div className="flex flex-col lg:flex-row bg-slate-50 rounded-3xl overflow-hidden shadow-2xl border border-slate-100 min-h-[600px]">
          <div className="lg:w-1/3 bg-slate-100 h-[500px] lg:h-auto overflow-y-auto custom-scrollbar">
            {CAPABILITIES.map((item, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`w-full text-left px-10 py-7 flex items-center gap-5 transition-all duration-500 border-l-4 ${activeTab === i ? 'bg-white border-yellow-500 shadow-xl text-slate-900 z-10' : 'text-slate-500 hover:bg-slate-200 border-transparent'}`}
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${activeTab === i ? 'bg-blue-900 text-white' : 'bg-slate-200 text-slate-400'}`}><item.icon size={24} /></div>
                <span className="font-bold text-lg">{item.title}</span>
                <ChevronRight size={20} className={`ml-auto transition-transform ${activeTab === i ? 'translate-x-1 opacity-100' : 'opacity-0'}`} />
              </button>
            ))}
          </div>
          <div className="lg:w-2/3 p-12 lg:p-20 bg-white flex flex-col justify-center relative">
            <RevealOnScroll direction="none" key={activeTab}>
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-8 text-blue-900">
                  <ActiveIcon size={48} className="text-yellow-500" />
                  <h3 className="text-4xl lg:text-5xl font-bold tracking-tight">{CAPABILITIES[activeTab].title}</h3>
                </div>
                <p className="text-xl text-slate-600 mb-12 leading-relaxed font-light">{CAPABILITIES[activeTab].description}</p>
                <h5 className="font-bold text-slate-900 uppercase tracking-widest mb-8 text-sm">Key Features & Services</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {CAPABILITIES[activeTab].features.map((f, i) => (
                    <div key={i} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl hover:bg-yellow-50 transition-colors border border-slate-100">
                      <CheckCircle2 size={24} className="text-yellow-600 flex-shrink-0" />
                      <span className="text-slate-700 font-bold">{f}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-16">
                  <a href="#contact" onClick={(e) => { e.preventDefault(); globalLenis?.scrollTo('#contact', { duration: 2.2 }); }} className="inline-flex items-center gap-3 bg-blue-900 text-white px-10 py-4 rounded-sm font-bold hover:bg-blue-800 transition-all shadow-xl hover:-translate-y-1">Learn More & Get Quote <ArrowRight size={20} /></a>
                </div>
              </div>
              <div className="absolute top-0 right-0 opacity-[0.03] pointer-events-none select-none">
                <ActiveIcon size={500} />
              </div>
            </RevealOnScroll>
          </div>
        </div>
      </div>
    </section>
  );
};

// --- Stats Section ---
const StatsSection: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) setVisible(true); }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-blue-900 text-white border-y border-white/10">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 text-center divide-x divide-white/10">
          {STATS.map((stat, i) => (
            <div key={i} className="px-6">
              <div className="text-5xl md:text-7xl font-black text-yellow-500 mb-4 tracking-tighter transition-all duration-1000 transform" style={{ opacity: visible ? 1 : 0, transform: visible ? 'scale(1)' : 'scale(0.8)' }}>
                {stat.value}
              </div>
              <div className="text-sm md:text-lg text-blue-100 uppercase tracking-[0.2em] font-bold">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// --- Projects Section ---
const ProjectsSection: React.FC = () => {
  const [selectedProject, setSelectedProject] = useState<ProjectItem | null>(null);

  return (
    <section id="projects" className="py-32 bg-slate-50">
      <div className="container mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-8">
          <RevealOnScroll direction="up">
            <h4 className="text-yellow-600 font-bold uppercase tracking-widest mb-4 text-sm">Portfolio</h4>
            <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 tracking-tight">Featured Projects</h2>
          </RevealOnScroll>
          <a href="#" className="inline-flex items-center gap-3 text-blue-900 font-bold border-b-2 border-blue-900 pb-1 hover:text-yellow-600 hover:border-yellow-600 transition-all text-lg group">View All Portfolio <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" /></a>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {PROJECTS.map((project, i) => (
            <RevealOnScroll key={i} direction="up" delay={i * 200}>
              <div
                className="group relative overflow-hidden rounded-2xl shadow-2xl h-[500px] cursor-pointer"
                onClick={() => setSelectedProject(project)}
              >
                <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/40 to-transparent p-12 flex flex-col justify-end">
                  <span className="bg-yellow-500 text-slate-900 px-4 py-1.5 rounded-sm font-black text-xs uppercase tracking-widest mb-6 w-fit shadow-xl">{project.category}</span>
                  <h3 className="text-4xl font-bold text-white mb-4 tracking-tight group-hover:text-yellow-400 transition-colors">{project.title}</h3>
                  <p className="text-slate-300 text-lg mb-8 line-clamp-2 font-light">{project.description}</p>
                  <div className="flex items-center gap-3 text-white font-black group-hover:gap-6 transition-all uppercase tracking-widest text-sm">View Project Details <ArrowRight size={20} className="text-yellow-500" /></div>
                </div>
              </div>
            </RevealOnScroll>
          ))}
        </div>
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/95 backdrop-blur-md animate-fadeIn">
          <div className="bg-white w-full max-w-5xl rounded-3xl overflow-hidden shadow-2xl relative max-h-[90vh] flex flex-col">
            <button onClick={() => setSelectedProject(null)} className="absolute top-6 right-6 z-20 bg-black/20 hover:bg-black/50 text-white p-3 rounded-full backdrop-blur-md transition-all"><X size={28} /></button>
            <div className="h-2/5 md:h-1/2 relative flex-shrink-0">
              <img src={selectedProject.image} alt={selectedProject.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent"></div>
              <div className="absolute bottom-8 left-8 text-white">
                <span className="text-yellow-400 font-black text-xs uppercase tracking-widest mb-2 block">{selectedProject.category}</span>
                <h2 className="text-4xl md:text-5xl font-bold tracking-tight">{selectedProject.title}</h2>
              </div>
            </div>
            <div className="p-10 md:p-14 overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                <div className="md:col-span-2">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3"><LayoutGrid className="text-yellow-500" /> Project Overview</h3>
                  <p className="text-slate-600 text-lg leading-relaxed font-light">{selectedProject.description}</p>
                </div>
                <div className="bg-slate-50 p-8 rounded-2xl border border-slate-100">
                  <h4 className="font-black text-slate-900 mb-6 uppercase tracking-widest text-sm border-b pb-4">Project Stats</h4>
                  <ul className="space-y-6">
                    <li><span className="block text-slate-400 text-xs uppercase font-black mb-1">Client</span><span className="font-bold text-slate-800 text-lg">{selectedProject.client}</span></li>
                    <li><span className="block text-slate-400 text-xs uppercase font-black mb-1">Location</span><span className="font-bold text-slate-800 flex items-center gap-2"><MapPin size={16} className="text-yellow-600" /> {selectedProject.location}</span></li>
                    <li><span className="block text-slate-400 text-xs uppercase font-black mb-1">Completion</span><span className="font-bold text-slate-800 flex items-center gap-2"><Calendar size={16} className="text-yellow-600" /> {selectedProject.date}</span></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// --- Sectors Section ---
const SectorsSection: React.FC = () => (
  <section id="sectors" className="py-32 bg-white">
    <div className="container mx-auto px-6">
      <div className="text-center mb-20">
        <RevealOnScroll direction="up">
          <h4 className="text-yellow-600 font-bold uppercase tracking-widest mb-4 text-sm">Industrial Presence</h4>
          <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 tracking-tight">Sectors We Serve</h2>
        </RevealOnScroll>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
        {SERVICES.map((service, i) => (
          <RevealOnScroll key={i} direction="up" delay={i * 150}>
            <div className="group relative h-[450px] rounded-2xl overflow-hidden shadow-xl border border-slate-100">
              <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
              <div className="absolute inset-0 bg-slate-900/60 group-hover:bg-blue-900/80 transition-all duration-500 flex flex-col items-center justify-center p-8 text-center">
                <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center text-yellow-400 mb-8 border border-white/20 group-hover:scale-110 transition-transform"><service.icon size={40} /></div>
                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-yellow-400 transition-colors tracking-tight">{service.title}</h3>
                <p className="text-slate-200 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform translate-y-4 group-hover:translate-y-0">{service.description}</p>
              </div>
            </div>
          </RevealOnScroll>
        ))}
      </div>
    </div>
  </section>
);

// --- Careers Section ---
const CareersSection: React.FC = () => (
  <section id="careers" className="py-32 bg-slate-50">
    <div className="container mx-auto px-6">
      <div className="flex flex-col lg:flex-row gap-20 items-center">
        <div className="lg:w-1/2">
          <RevealOnScroll direction="right">
            <h4 className="text-yellow-600 font-bold uppercase tracking-widest mb-4 text-sm">Join Our Team</h4>
            <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 mb-10 tracking-tight">Build Your Future with Us</h2>
            <p className="text-slate-600 mb-12 text-xl font-light leading-relaxed">We are constantly seeking passionate, skilled individuals to join our multicultural team and drive industrial innovation forward.</p>
            <div className="space-y-4">
              {["Senior Civil Engineer", "Project Manager", "Safety Officer", "Structural Designer"].map((job, idx) => (
                <div key={idx} className="p-6 bg-white rounded-2xl flex justify-between items-center border border-slate-200 hover:border-yellow-500 hover:shadow-xl transition-all group cursor-pointer">
                  <div className="flex items-center gap-5">
                    <div className="w-12 h-12 bg-slate-50 text-blue-900 rounded-xl flex items-center justify-center group-hover:bg-blue-900 group-hover:text-white transition-colors"><Briefcase size={24} /></div>
                    <div><h5 className="font-bold text-slate-900 text-lg group-hover:text-blue-900 transition-colors">{job}</h5><p className="text-xs text-slate-500 font-bold uppercase tracking-widest mt-1">Saudi Arabia • Full-Time • Posted 2d ago</p></div>
                  </div>
                  <button className="bg-slate-100 text-blue-900 hover:bg-yellow-500 hover:text-blue-900 px-6 py-3 rounded-lg font-bold text-sm transition-all shadow-sm">Apply</button>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </div>
        <div className="lg:w-1/2 relative">
          <RevealOnScroll direction="left" delay={300}>
            <div className="relative z-10 overflow-hidden rounded-2xl shadow-2xl">
              <img src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&q=80&w=2084" className="w-full h-auto transition-transform duration-1000 hover:scale-105" alt="Careers" />
              <div className="absolute inset-0 bg-blue-900/30"></div>
            </div>
            <div className="absolute -bottom-10 -left-10 bg-white p-10 rounded-2xl shadow-2xl border-r-8 border-yellow-500 hidden xl:block z-20">
              <div className="flex items-center gap-4 mb-4"><Users className="text-blue-900" size={32} /><h4 className="text-2xl font-bold text-slate-900">Multicultural Team</h4></div>
              <p className="text-slate-500 text-sm font-medium">Over 2,500 professionals from 15+ nations working together towards a common vision.</p>
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </div>
  </section>
);

// --- Testimonials Section ---
const TestimonialsSection: React.FC = () => (
  <section className="py-32 bg-white overflow-hidden relative">
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-[0.03] select-none flex items-center justify-center">
      <Quote size={800} />
    </div>
    <div className="container mx-auto px-6 relative z-10">
      <div className="text-center mb-20">
        <RevealOnScroll direction="up">
          <h4 className="text-yellow-600 font-bold uppercase tracking-widest mb-4 text-sm">Testimonials</h4>
          <h2 className="text-4xl lg:text-6xl font-bold text-slate-900 tracking-tight">Voices of Excellence</h2>
        </RevealOnScroll>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {TESTIMONIALS.map((item, i) => (
          <RevealOnScroll key={i} direction="up" delay={i * 200} className="bg-slate-50 p-12 rounded-3xl relative border border-slate-100 hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 group">
            <Quote className="absolute top-10 right-12 text-yellow-500/20 w-16 h-16 group-hover:scale-110 transition-transform" />
            <p className="text-2xl text-slate-600 italic leading-relaxed mb-10 font-serif relative z-10">"{item.quote}"</p>
            <div className="flex items-center gap-6">
              <img src={item.image} alt={item.author} className="w-20 h-20 rounded-2xl object-cover border-4 border-white shadow-lg" />
              <div>
                <h5 className="font-bold text-slate-900 text-xl">{item.author}</h5>
                <span className="text-sm text-yellow-600 font-black uppercase tracking-widest block mt-1">{item.role}</span>
                <span className="text-sm text-slate-400 font-bold">{item.company}</span>
              </div>
            </div>
          </RevealOnScroll>
        ))}
      </div>
    </div>
  </section>
);

// --- Contact Section ---
const ContactSection: React.FC = () => (
  <section id="contact" className="py-32 bg-slate-900 text-white relative">
    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-500 via-red-600 to-blue-900"></div>
    <div className="container mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-24">
        <div>
          <RevealOnScroll direction="right">
            <h4 className="text-yellow-500 font-bold uppercase tracking-widest mb-4 text-sm">Get In Touch</h4>
            <h2 className="text-4xl lg:text-7xl font-bold mb-10 tracking-tight leading-tight">Let's Discuss <br /> Your Project</h2>
            <p className="text-slate-400 text-xl mb-12 font-light leading-relaxed">Ready to start your next industrial landmark? Our experts are here to provide tailored engineering and construction consulting.</p>
            <div className="space-y-8">
              <div className="flex items-center gap-6 group">
                <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center text-yellow-500 group-hover:bg-yellow-500 group-hover:text-slate-900 transition-all shadow-xl"><Phone size={32} /></div>
                <div><h5 className="font-bold text-xl mb-1 group-hover:text-yellow-500 transition-colors">Call Our HQ</h5><p className="text-slate-400 font-medium">{COMPANY_INFO.phone}</p></div>
              </div>
              <div className="flex items-center gap-6 group">
                <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center text-yellow-500 group-hover:bg-yellow-500 group-hover:text-slate-900 transition-all shadow-xl"><Mail size={32} /></div>
                <div><h5 className="font-bold text-xl mb-1 group-hover:text-yellow-500 transition-colors">Email Us</h5><p className="text-slate-400 font-medium">{COMPANY_INFO.email}</p></div>
              </div>
              <div className="flex items-center gap-6 group">
                <div className="w-16 h-16 bg-slate-800 rounded-2xl flex items-center justify-center text-yellow-500 group-hover:bg-yellow-500 group-hover:text-slate-900 transition-all shadow-xl"><MapPin size={32} /></div>
                <div><h5 className="font-bold text-xl mb-1 group-hover:text-yellow-500 transition-colors">Location</h5><p className="text-slate-400 font-medium">{COMPANY_INFO.location}</p></div>
              </div>
            </div>
          </RevealOnScroll>
        </div>
        <div className="bg-white p-12 lg:p-16 rounded-3xl shadow-2xl text-slate-900 relative">
          <RevealOnScroll direction="left" delay={300}>
            <h3 className="text-3xl font-bold mb-10 tracking-tight">Direct Inquiry</h3>
            <form className="space-y-8" onSubmit={(e) => e.preventDefault()}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400">Full Name</label>
                  <input type="text" placeholder="John Doe" className="w-full px-6 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-900 outline-none transition-all font-bold" />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-black uppercase tracking-widest text-slate-400">Email Address</label>
                  <input type="email" placeholder="john@company.com" className="w-full px-6 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-900 outline-none transition-all font-bold" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">Project Type</label>
                <select className="w-full px-6 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-900 outline-none transition-all font-bold appearance-none bg-white">
                  <option>General Construction</option>
                  <option>Oil & Gas Services</option>
                  <option>MEP & Electrical</option>
                  <option>Manpower Supply</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-black uppercase tracking-widest text-slate-400">Message</label>
                <textarea placeholder="Tell us about your project requirements..." rows={5} className="w-full px-6 py-4 rounded-xl border border-slate-200 focus:ring-4 focus:ring-blue-100 focus:border-blue-900 outline-none transition-all font-bold resize-none"></textarea>
              </div>
              <button className="w-full bg-gradient-to-r from-red-700 to-red-900 text-white font-black py-5 rounded-xl hover:from-red-600 hover:to-red-800 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 flex items-center justify-center gap-3 uppercase tracking-widest text-sm">Send Inquiry <Send size={20} /></button>
            </form>
          </RevealOnScroll>
        </div>
      </div>
    </div>
  </section>
);

// --- Footer Component ---
const Footer: React.FC = () => (
  <footer className="bg-slate-950 text-slate-500 py-24 border-t border-slate-900">
    <div className="container mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
        <div className="col-span-1 lg:col-span-1">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-blue-900 text-white flex items-center justify-center font-bold text-2xl rounded-xl">{COMPANY_INFO.logoChar}</div>
            <span className="text-2xl font-black text-white uppercase tracking-tighter">{COMPANY_INFO.name}</span>
          </div>
          <p className="leading-relaxed mb-10 font-medium">{COMPANY_INFO.description}</p>
          <div className="flex gap-6">
            <Facebook className="hover:text-yellow-500 cursor-pointer transition-colors" size={24} />
            <Linkedin className="hover:text-yellow-500 cursor-pointer transition-colors" size={24} />
            <Twitter className="hover:text-yellow-500 cursor-pointer transition-colors" size={24} />
            <Instagram className="hover:text-yellow-500 cursor-pointer transition-colors" size={24} />
          </div>
        </div>
        <div>
          <h4 className="text-white font-black uppercase tracking-widest text-sm mb-8">Quick Links</h4>
          <ul className="space-y-4 font-bold">
            {['About Company', 'Our Sectors', 'Sustainability', 'Project Portfolio', 'Careers'].map(link => (
              <li key={link}><a href="#" className="hover:text-yellow-500 flex items-center gap-2 group transition-colors"><ChevronRight size={14} className="text-yellow-600 group-hover:translate-x-1 transition-transform" /> {link}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-black uppercase tracking-widest text-sm mb-8">Services</h4>
          <ul className="space-y-4 font-bold">
            {['General Contracting', 'Oil & Gas Support', 'Manpower Supply', 'MEP Solutions', 'Civil Works'].map(link => (
              <li key={link}><a href="#" className="hover:text-yellow-500 flex items-center gap-2 group transition-colors"><ChevronRight size={14} className="text-yellow-600 group-hover:translate-x-1 transition-transform" /> {link}</a></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-black uppercase tracking-widest text-sm mb-8">Newsletter</h4>
          <p className="text-sm mb-6 font-medium">Get our latest project updates and corporate news.</p>
          <div className="flex gap-2">
            <input type="email" placeholder="Email" className="bg-slate-900 border border-slate-800 px-4 py-3 rounded-lg w-full outline-none focus:border-yellow-500 transition-colors text-white" />
            <button className="bg-yellow-500 text-slate-900 p-3 rounded-lg hover:bg-yellow-400 transition-colors shadow-lg"><Send size={20} /></button>
          </div>
        </div>
      </div>
      <div className="border-t border-slate-900 pt-12 flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="text-xs font-bold uppercase tracking-widest">&copy; {new Date().getFullYear()} {COMPANY_INFO.name}. All Rights Reserved.</div>
        <div className="flex gap-10 text-xs font-bold uppercase tracking-widest">
          <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
          <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
          <a href="#" className="hover:text-white transition-colors">Cookie Policy</a>
        </div>
      </div>
    </div>
  </footer>
);

const ScrollToTop: React.FC = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const handle = () => setShow(window.scrollY > 800);
    window.addEventListener('scroll', handle);
    return () => window.removeEventListener('scroll', handle);
  }, []);
  return (
    <button
      onClick={() => globalLenis?.scrollTo(0, { duration: 2.5 })}
      className={`fixed bottom-10 right-10 z-50 w-16 h-16 bg-yellow-500 text-slate-900 rounded-2xl shadow-2xl transition-all duration-700 flex items-center justify-center hover:bg-yellow-400 hover:scale-110 active:scale-90 border-4 border-white ${show ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'}`}
    >
      <ArrowUp size={32} strokeWidth={3} />
    </button>
  );
};

// --- Main App Component ---
export default function App() {
  useEffect(() => {
    if (window.Lenis) {
      const lenis = new window.Lenis({
        duration: 2.2,
        easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        direction: 'vertical',
        gestureDirection: 'vertical',
        smooth: true,
        mouseMultiplier: 1.15,
        smoothTouch: false,
        touchMultiplier: 2,
        infinite: false,
      });

      globalLenis = lenis;

      const raf = (time: number) => {
        lenis.raf(time);
        requestAnimationFrame(raf);
      };

      requestAnimationFrame(raf);

      return () => {
        lenis.destroy();
        globalLenis = null;
      };
    }
  }, []);

  return (
    <div className="font-sans text-slate-800 antialiased selection:bg-yellow-500 selection:text-white">
      <Header />
      <main>
        <Hero />
        <AboutSection />
        <WhyChooseUsSection />
        <SustainabilitySection />
        <WhatWeDoSection />
        <StatsSection />
        <SectorsSection />
        <ProjectsSection />
        <CareersSection />
        <TestimonialsSection />
        <ContactSection />
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}
